import { useState, useEffect } from 'react';
import type { Application } from '../types';

interface AppDetailModalProps {
  app: Application | null;
  onClose: () => void;
}

export default function AppDetailModal({ app, onClose }: AppDetailModalProps) {
  const [showFullContent, setShowFullContent] = useState(false);

  useEffect(() => {
    if (app) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [app]);

  if (!app) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">应用详情</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-6 mb-6">
            <img
              src={app.icon}
              alt={app.name}
              className="w-24 h-24 rounded-xl object-cover border border-gray-200"
            />
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-2xl font-bold text-gray-900">{app.name}</h4>
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  app.type === 'cloud' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {app.type === 'cloud' ? '云电脑应用' : 'Web应用'}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                <span>版本：{app.version}</span>
                <span>开发者：{app.developer}</span>
                <span>下载量：{app.downloads.toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {app.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">应用分类</label>
              <div className="text-sm text-gray-900">{app.category}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">应用来源</label>
              <div className="text-sm text-gray-900">
                {app.source === 'platform' ? '平台上传' : '用户上传'}
              </div>
            </div>
            {app.type === 'cloud' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">应用路径</label>
                  <div className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-lg">
                    {app.path}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">启动路径</label>
                  <div className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-lg">
                    {app.launchPath}
                  </div>
                </div>
              </>
            )}
            {app.registryKey && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">注册表项</label>
                <div className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-lg">
                  {app.registryKey}
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">应用简述</label>
            <div className="text-sm text-gray-700 leading-relaxed bg-gray-50 px-4 py-3 rounded-lg">
              {app.description}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">应用详述</label>
            <div className="text-sm text-gray-700 leading-relaxed bg-gray-50 px-4 py-3 rounded-lg">
              {showFullContent ? (
                <div>{app.detailedDescription}</div>
              ) : (
                <div>
                  {app.detailedDescription.length > 200
                    ? `${app.detailedDescription.substring(0, 200)}...`
                    : app.detailedDescription}
                </div>
              )}
              {app.detailedDescription.length > 200 && (
                <button
                  onClick={() => setShowFullContent(!showFullContent)}
                  className="text-teal-600 hover:text-teal-700 mt-2 text-sm font-medium"
                >
                  {showFullContent ? '收起' : '展开全部'}
                  <i className={`ri-arrow-${showFullContent ? 'up' : 'down'}-s-line ml-1`}></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium whitespace-nowrap"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
